import { useState, useEffect, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import RoundedButton from '../components/buttons/RoundedButton'
import RoundedButtonLink from '../components/buttons/RoundedButtonLink'
import FormInput from '../components/inputs/FormInput'
import DropdownInput from '../components/inputs/DropdownInput'
import LoadingModal from '../components/modals/LoadingModal'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import arrow from '../assets/arrow.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'

const EditDealer = () => {
    const { id } = useParams()

    const [entities, setEntities] = useState([])
    const [harbors, setHarbors] = useState([])

    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(null)

    const [username, setUsername] = useState('')
    const [usernameError, setUsernameError] = useState(null)

    const [email, setEmail] = useState('')
    const [emailError, setEmailError] = useState(null)

    const [selectedEntity, setSelectedEntity] = useState(null)
    const [selectedEntityError, setSelectedEntityError] = useState(null)
    const [entitySelected, setEntitySelected] = useState(true)

    const [selectedHarbor, setSelectedHarbor] = useState(null)
    const [selectedHarborError, setSelectedHarborError] = useState(null)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role !== Constants.Roles.ADMINIT) {
                navigate('/')
            } else {
                getEntities()
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get entities
    const getEntities = () => {
        fetch('/api/Backoffice/ListEntities', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            if (response.status === 200) {
                const jsonRes = await response.json()

                const arrayEntities = jsonRes.map((option) => ({ value: option.entity.id, label: option.entity.name }))

                setEntities(arrayEntities)

                getUserDetails(arrayEntities)
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(strings.getEntitiesError)

                    setEntities([])
                }, 1000)
            }

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setEntities([])
            }, 1000)   
        })
    }

    // Handle get user details
    const getUserDetails = (arrayEntities) => {
        fetch(`/api/Backoffice/${id}/Details`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            if (response.status === 200) {
                const jsonRes = await response.json()

                setName(jsonRes.name)
                setUsername(jsonRes.username)
                setEmail(jsonRes.email)
                setSelectedEntity(arrayEntities.find((entity) => entity.label === jsonRes.entityName))

                getHarbors(jsonRes.harborName, arrayEntities.find((entity) => entity.label === jsonRes.entityName).value)
            } else {
                showErrorToast(strings.getUserDetailsError)
                resetVariables()
            }
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                resetVariables()
            }, 1000)
        })
    }

    // Handle get harbors
    const getHarbors = (userHarborName, entityId) => {
        fetch(`/api/Backoffice/${entityId}/GetHarbors`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()
    
                    const arrayHarbors = jsonRes.map((option) => ({value: option.harborId, label: option.harborName }))

                    setSelectedHarbor(arrayHarbors.find((harbor) => harbor.label === userHarborName))
    
                    setHarbors(arrayHarbors)
                } else if (response.status === 404) {
                    const errorMessage = await response.text()

                    setHarbors([])
                    setSelectedHarbor(null)

                    if(errorMessage === '"No ports found."') {
                        showErrorToast(strings.getHarborsByEntityError)
                    } else {
                        showErrorToast(strings.getDropdownsError)
                    }

                } else {
                    setIsLoading(false)
                    showErrorToast(strings.getDropdownsError)

                    setHarbors([])
                    setSelectedHarbor(null)
                }
            }, 1000)  

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setHarbors([])
                setSelectedHarbor(null)
            }, 1000)   
        })
    }

    // Handle get harbors by entity
    const getHarborsByEntity = (entityId) => {
        setIsLoading(true)

        fetch(`/api/Backoffice/${entityId}/GetHarbors`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()
    
                    const arrayHarbors = jsonRes.map((option) => ({value: option.harborId, label: option.harborName }))
    
                    setHarbors(arrayHarbors)
                    setSelectedHarbor(null)
                } else if (response.status === 404) {
                    const errorMessage = await response.text()

                    setHarbors([])
                    setSelectedHarbor(null)

                    if(errorMessage === '"No ports found."') {
                        showErrorToast(strings.getHarborsByEntityError)
                    } else {
                        showErrorToast(strings.getDropdownsError)
                    }

                } else {
                    setIsLoading(false)
                    showErrorToast(strings.getDropdownsError)

                    setHarbors([])
                    setSelectedHarbor(null)
                }
            }, 1000)  

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setHarbors([])
                setSelectedHarbor(null)
            }, 1000)   
        })
    }

    // Handle changes in harbor dropdown
    const onChangeSelectedHarbor = (item) => {
        setSelectedHarbor(item)
        setSelectedHarborError(null)
    }

    // Handle changes in name input text
    const onChangeName = (nameText) => {
        setNameError(null)
        setName(nameText.target.value)
    }

    // Handle changes in name input text
    const onChangeUsername = (usernameText) => {
        setUsernameError(null)
        setUsername(usernameText.target.value)
    }

    // Handle changes in email input text
    const onChangeEmail = (emailText) => {
        setEmailError(null)
        setEmail(emailText.target.value)
    }

    // Handle changes in harbor dropdown
    const onChangeSelectedEntity = (item) => {
        setSelectedEntity(item)
        setSelectedEntityError(null)
        setEntitySelected(true)
        getHarborsByEntity(item.value)
    }

    // Check if email is valid
    const isValidEmail = (email) => {
        const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
        return emailPattern.test(email)
    }

    // Update user dealer and navigate back to Users list
    const updateDealer = () => {
        let hasErrors = false

        if (email.trim().length === 0) {
            setEmailError(strings.insertEmailError)
            hasErrors = true
        } else if (!isValidEmail(email)) {
            hasErrors = true
            setEmailError(strings.invalidEmail)
        }
    
        if (name.trim().length === 0) {
            hasErrors = true
            setNameError(strings.insertNameError)
        }

        if (username.trim().length === 0) {
            hasErrors = true
            setUsernameError(strings.insertUsernameError)
        }

        if(!selectedHarbor) {
            hasErrors = true
            setSelectedHarborError(strings.selectAffectedShipyardError)
        }

        if(!selectedEntity) {
            hasErrors = true
            setSelectedEntityError(strings.selectEntityError)
        }

        if (!hasErrors) {
            setIsLoading(true)

            const data = {
                HarborId: selectedHarbor.value,
                EntityId: selectedEntity.value,
                Name: name,
                Username: username,
                Email: email
            }

            // Update user dealer
            fetch(`/api/Backoffice/${id}/EditUser`, {
                method:'PUT',
                headers: { 
                  'Accept': 'application/json',
                  'Content-Type': 'application/json; charset=UTF-8',
                  'Authorization': 'Bearer ' + loggedUser.jwtToken
                }, 
                mode: 'cors',
                body: JSON.stringify(data)
            }).then(async response => {
                setTimeout(() => {
                    setIsLoading(false)

                    if (response.status === 200) {
                        navigate(-1)
                    } else {
                        showErrorToast(strings.updateUserError)
                    }
                }, 1000)

            }).catch((error) => {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(error)
                }, 1000)
            })
        }
    }

    // Resets local state variables
    const resetVariables = () => {
        setName('')
        setUsername('')
        setEmail('')
        setSelectedHarbor(null)
        setSelectedEntity(null)
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }
    
    return (
        <>
            <TopNavBar />

            <div className='flex flex-col px-[12.5rem] min-h-screen mb-6'>

                { isLoading ?
                    <LoadingModal />
                :   <>
                        <div className='flex flex-row items-center mt-16 mb-12 space-x-2'>
                            <button onClick={() => navigate(-1)}>
                                <img className='h-5 w-5' src={arrow} alt='Voltar atrás'/>
                            </button>
                            <p className='font-montserrat font-semibold text-accent'>{strings.updateUser} / {strings.dealer}</p>
                        </div>

                        <p className='font-montserrat text-xl font-bold text-navy'>{strings.info}</p>

                        <div className='flex flex-row mt-4 space-x-6'>
                            <div className='flex flex-1 flex-col'>
                                <p className='font-montserrat font-semibold text-sm'>{strings.entity}</p>
                                <DropdownInput 
                                    options={entities}
                                    value={selectedEntity}
                                    onChange={onChangeSelectedEntity}
                                    error={selectedEntityError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{selectedEntityError ? selectedEntityError : ''}</p>
                            </div>

                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='name' className='font-montserrat font-semibold text-sm'>{strings.name}</label>
                                <FormInput 
                                    id='name' 
                                    type='text'
                                    value={name}
                                    placeholder={strings.insertName}
                                    onChange={onChangeName}
                                    error={nameError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{nameError ? nameError : ''}</p>
                            </div>
                        </div>

                        <div className='flex flex-row mt-9 space-x-6'>
                            <div className='flex flex-1 flex-col'>
                                <p className='font-montserrat font-semibold text-sm' style={{ color: entitySelected ? '#000' : 'rgba(0, 0, 0, 0.38)'}}>{strings.affectedShipyard}</p>
                                <DropdownInput
                                    options={harbors}
                                    value={selectedHarbor}
                                    onChange={onChangeSelectedHarbor}
                                    error={selectedHarborError}
                                    disabled={entitySelected ? false : true}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{selectedHarborError ? selectedHarborError : ''}</p>
                            </div>

                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='username' className='font-montserrat font-semibold text-sm'>{strings.username}</label>
                                <FormInput 
                                    id='username' 
                                    type='text'
                                    value={username}
                                    placeholder={strings.insertUsername}
                                    onChange={onChangeUsername}
                                    error={usernameError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{usernameError ? usernameError : ''}</p>

                            </div>
                        </div>

                        <div className='flex flex-row mt-9 space-x-6'>
                            <div className='flex flex-1 flex-col'>
                                <label htmlFor='email' className='font-montserrat font-semibold text-sm'>{strings.email}</label>
                                <FormInput 
                                    id='email' 
                                    type='email'
                                    value={email}
                                    placeholder={strings.insertEmail}
                                    onChange={onChangeEmail}
                                    error={emailError}
                                />
                                <p className='font-montserrat text-sm font-medium text-negative mt-1.5 h-2'>{emailError ? emailError : ''}</p>
                            </div>
                            
                            <div className='flex flex-1 flex-col'/>
                        </div>

                        <div className='flex flex-row items-center mt-10 space-x-6'>
                            <div className='flex w-60'>
                                <RoundedButtonLink 
                                    path='/backoffice-users'
                                    text={strings.cancel}
                                    height={48}
                                    bgColor='#E85F5C'
                                    flex={true}
                                />
                            </div>

                            <div className='flex w-60'>
                                <RoundedButton 
                                    onClick={updateDealer}
                                    text={strings.save}
                                    height={48}
                                    bgColor='#007EB4'
                                />
                            </div>
                        
                        </div>

                    </>
                }
    
            </div>

            <ToastContainer />
        </>
    )
}

export default EditDealer